<template>
  <q-dialog v-model="isOpen" persistent class="confirm-modal">
    <q-card flat :style="cardStyle" class="light-bordered light-radius q-px-xs">
      <q-card-section class="row items-center q-pb-none q-pr-sm">
        <h5 class="text-primary-dark text-font-medium">{{ confirmModalData?.title }}</h5>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
          :disable="isConfirmLoading"
          color="secondary"
          @click="handleCancel"
        />
      </q-card-section>

      <q-card-section class="row items-center no-wrap q-pt-md">
        <div class="round bg-secondary-light q-pa-sm q-mr-md">
          <q-icon :name="confirmModalData?.icon" size="lg" color="white" />
        </div>
        <div class="column">
          <span class="text-18">{{ confirmModalData?.message }}</span>
        </div>
      </q-card-section>

      <q-card-section
        v-if="confirmModalData?.requiresTypeConfirmation"
        class="confirm-container q-pb-lg"
      >
        <div>
          <p class="q-mb-sm text-16">
            Type
            <span class="text-font-medium">{{ confirmModalData?.typeConfirmationText }} </span> to
            confirm
          </p>
          <q-input
            v-model="typeConfirmationInput"
            outlined
            dense
            :placeholder="`Type the company name here`"
            class="text-16"
            @keyup.enter="handleConfirm"
          />
        </div>
      </q-card-section>

      <q-card-section v-if="confirmModalData?.confirmInput" class="confirm-container q-pb-lg">
        <p class="q-mb-sm text-16">{{ confirmModalData.confirmInput.label }}</p>
        <q-input
          v-model="confirmInputValue"
          :type="confirmInputType"
          outlined
          :dense="confirmInputType === 'text'"
          :rows="confirmInputType === 'textarea' ? confirmInputRows : undefined"
          :placeholder="confirmModalData.confirmInput.placeholder"
          :rules="confirmInputRules"
          :class="[
            'text-16',
            confirmInputType === 'text' ? 'confirm-text-input' : 'confirm-note-textarea',
          ]"
          @keyup.enter="onInputEnter"
        />
      </q-card-section>

      <q-card-actions align="right" class="confirm-actions bg-white q-pa-md text-primary">
        <q-btn
          v-close-popup
          :label="confirmModalData?.cancelText"
          :color="confirmModalData?.cancelColor"
          outline
          class="light-bordered light-radius"
          padding="0.25rem 1rem"
          no-caps
          :disable="isConfirmLoading"
          @click="handleCancel"
        />
        <q-space />
        <q-btn
          class="light-bordered light-radius"
          :class="{
            'text-white': confirmModalData?.confirmColor === 'warning',
            'text-primary': confirmModalData?.confirmColor !== 'warning',
          }"
          :label="confirmModalData?.confirmText"
          :color="confirmModalData?.confirmColor"
          padding="0.25rem 1.25rem"
          unelevated
          no-caps
          :disable="isConfirmDisabled || isConfirmLoading"
          :loading="isConfirmLoading"
          @click="handleConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useConfirm } from 'src/composables/app/useConfirm'

const { isOpen, confirmModalData, closeConfirm } = useConfirm()
const typeConfirmationInput = ref('')
const confirmInputValue = ref('')
const isConfirmLoading = ref(false)

const cardStyle = computed(() => ({
  minWidth: '350px',
  maxWidth: confirmModalData.value?.confirmInput ? '500px' : '450px',
}))

const confirmInputType = computed(() => confirmModalData.value?.confirmInput?.type || 'textarea')
const confirmInputRows = computed(() => confirmModalData.value?.confirmInput?.rows ?? 3)
const confirmInputRules = computed(() => confirmModalData.value?.confirmInput?.rules || [])

const isInputValid = computed(() =>
  confirmInputRules.value.every((rule) => rule(confirmInputValue.value) === true),
)

const isConfirmDisabled = computed(() => {
  if (
    confirmModalData.value?.requiresTypeConfirmation &&
    typeConfirmationInput.value !== confirmModalData.value?.typeConfirmationText
  ) {
    return true
  }
  if (confirmInputRules.value.length && !isInputValid.value) return true
  return false
})

const onInputEnter = () => {
  if (confirmInputType.value === 'text') handleConfirm()
}

// Reset input and loading state when modal opens/closes
watch(isOpen, (newValue) => {
  if (newValue && confirmModalData.value?.confirmInput) {
    confirmInputValue.value = confirmModalData.value.confirmInput.initialValue ?? ''
  } else if (!newValue) {
    typeConfirmationInput.value = ''
    confirmInputValue.value = ''
    isConfirmLoading.value = false
  }
})

const handleConfirm = async () => {
  if (isConfirmLoading.value || isConfirmDisabled.value) return

  try {
    isConfirmLoading.value = true
    const payload = confirmModalData.value?.confirmInput
      ? { inputValue: confirmInputValue.value.trim() }
      : undefined
    await confirmModalData.value.onConfirm(payload)
    closeConfirm()
  } catch (error) {
    console.error('Error in confirm action:', error)
  } finally {
    isConfirmLoading.value = false
  }
}

const handleCancel = () => {
  confirmModalData.value?.onCancel?.()
  closeConfirm()
}
</script>

<style lang="scss">
@import 'src/css/setup/_variables.scss';

// Confirmation Modal Styles
.confirm-modal {
  z-index: 35000; // Higher than CreateModal's 30000

  .q-dialog__actions {
    padding: 1rem;
    justify-content: center;
    gap: 0.5rem;
  }

  .confirm-container {
    border-top: 1px solid $clr-border;
    border-bottom: 1px solid $clr-border;
  }
  .round {
    min-width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .confirm-note-textarea .q-field__control {
    min-height: 5rem;
  }

  .confirm-note-textarea textarea {
    resize: vertical;
    line-height: 1.4;
  }
}
</style>
